/**
 * F-126/F-133 BGM track panel — a docked card (not a modal), for the same
 * reason the sections panel is one: the thing it talks about is the TABLE.
 * "Joke has one stem" is only actionable while you can see the Joke rows and
 * the 1/2 chip sitting on them.
 *
 * It answers ONE question — how far along is the scoring — because that is now
 * all there is to the music track: coverage, and the moods still short of
 * stems. There is no "request review" and no approval any more (F-133); the
 * work is done when every mood has `BGM_MIN_STEMS` stems (F-139), and the tag
 * on the board's chip and its dashboard card says so on its own.
 *
 * The panel reads the LIVE doc rather than the board record: the designer
 * filling slots wants the number to move as they drop each one, not two
 * seconds later when the server's copy catches up.
 */
import { BGM_MIN_STEMS, boardBgmCoverage } from "@kola/shared";
import { useStore } from "zustand";

import type { BoardSession } from "../../core/session.js";
import { useDocVersion } from "../board/useDocVersion.js";
import { CloseButton } from "../../ui/CloseButton.js";
import { coverageSummary, missingRuns } from "./layout.js";
import { bgmUi } from "./store.js";

export interface BgmPanelProps {
  session: BoardSession;
}

export function BgmPanel({ session }: BgmPanelProps) {
  const target = useStore(bgmUi, (s) => s.target);
  useDocVersion(session);
  if (target === null || target.boardId !== session.boardId) return null;

  const close = () => bgmUi.getState().close();
  const coverage = boardBgmCoverage(session.board, target.sheetId);
  const gaps = missingRuns(coverage);

  return (
    <div id="board-music-details" className="bgm-panel" role="dialog" aria-label="BGM track">
      <div className="bgm-panel-head">
        <span className="bgm-panel-title">BGM stems</span>
        <span className="spacer" />
        <CloseButton onClick={close} />
      </div>

      <div className={`bgm-gate bgm-gate-${coverage.ready ? "approved" : "in_progress"}`}>
        <span className="bgm-gate-name">Music</span>
        <span className="bgm-gate-state">{coverage.ready ? "All moods scored" : "Scoring"}</span>
      </div>
      <div className="bgm-coverage">{coverageSummary(coverage)}</div>

      {gaps.length > 0 && (
        <ul className="bgm-gap-list">
          {gaps.map((run_) => (
            <li key={run_.topRowId} className="bgm-gap">
              <span className="bgm-gap-title">{run_.title}</span>
              <span className="bgm-gap-hint">
                {run_.stems} of {BGM_MIN_STEMS} stems
                {run_.misplacedStem ? " · music is not on the first row" : ""}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="bgm-panel-note">
        Music is optional and runs alongside the shoot. Give each mood at least {BGM_MIN_STEMS}{" "}
        stems on its first row, so there is something to choose between; nobody has to sign it
        off.
      </div>
    </div>
  );
}
